<script lang="ts">
  import './app.css';
  import { QueryClientProvider } from '@tanstack/svelte-query';
  import { getQueryClient } from '$lib/state/queryClient';
  import { ensureSession, logout as apiLogout } from '$lib/api/auth';
  import { fetchMe } from '$lib/api/me';
  import { session } from '$lib/state/session.svelte';
  import { route } from '$lib/state/route.svelte';
  import { getErrorMessage } from '$lib/api/client';
  import Login from '$lib/components/Login.svelte';
  import Shell from '$lib/components/shell/Shell.svelte';
  import ProjectsOverviewPage from '$lib/components/admin/ProjectsOverviewPage.svelte';
  import TicketInboxPage from '$lib/components/tickets/TicketInboxPage.svelte';
  import TicketDetailPage from '$lib/components/tickets/TicketDetailPage.svelte';
  import DeploysPage from '$lib/components/admin/DeploysPage.svelte';
  import DailyActivityPage from '$lib/components/admin/DailyActivityPage.svelte';
  import UsersPage from '$lib/components/admin/UsersPage.svelte';
  import ProjectWorkspacePage from '$lib/components/workspace/ProjectWorkspacePage.svelte';
  import DocReaderPage from '$lib/components/docs/DocReaderPage.svelte';
  import SettingsPage from '$lib/components/SettingsPage.svelte';
  import Toast from '$lib/components/ui/Toast.svelte';
  import CommandPalette from '$lib/components/palette/CommandPalette.svelte';
  import EmptyState from '$lib/components/ui/EmptyState.svelte';
  import { FolderKanban, ShieldAlert } from 'lucide-svelte';

  const queryClient = getQueryClient();

  let isAuthenticated = $state(false);
  let isBootstrapping = $state(true);
  let bootError = $state('');
  const current = $derived(route.current);

  // Deep-link + refresh survival: the hash was already parsed into
  // route.current at module load, before auth even resolved, so simply not
  // touching it here is what "honor it after login" means — it's free.
  // The only thing we do fix up is canonicalizing/redirecting for routes
  // that turn out to be invalid once we know who's logged in (e.g. an
  // admin-only tab for a non-admin) — that's a programmatic redirect, so
  // replace not push.
  function normalizeRouteAfterAuth() {
    const r = route.current;
    if (session.isAdmin) {
      route.replace(r);
      return;
    }
    // Non-admin landing on an admin-only tab (or the bare default route,
    // which parses to 'overview'): send them to their first granted
    // project's board — same as the prototype's defaultRoute(). If they
    // have none, land on 'overview' anyway; it renders the "No projects
    // yet" empty state for non-admins instead of the admin overview.
    const adminOnly =
      r.tab === 'overview' ||
      r.tab === 'inbox' ||
      r.tab === 'deploys' ||
      r.tab === 'daily' ||
      r.tab === 'users';
    if (adminOnly) {
      const firstProject = session.me?.projects[0];
      route.replace(firstProject ? { tab: 'project', projectId: firstProject.id } : { tab: 'overview' });
    } else {
      route.replace(r);
    }
  }

  async function bootstrap() {
    isBootstrapping = true;
    bootError = '';
    try {
      const me = await fetchMe();
      session.setMe(me);
      isAuthenticated = true;
      normalizeRouteAfterAuth();
    } catch (err) {
      bootError = getErrorMessage(err);
      isAuthenticated = false;
    } finally {
      isBootstrapping = false;
    }
  }

  async function checkAuth() {
    const ok = await ensureSession();
    if (ok) {
      await bootstrap();
    } else {
      isBootstrapping = false;
    }
  }

  async function handleAuthenticated() {
    await bootstrap();
  }

  async function logout() {
    await apiLogout();
    session.clear();
    isAuthenticated = false;
    queryClient.clear();
    route.replace({ tab: 'overview' });
  }

  checkAuth();
</script>

<svelte:head>
  <title>Genba</title>
</svelte:head>

<Toast />

{#if isBootstrapping}
  <div class="min-h-screen flex items-center justify-center text-t3">Loading...</div>
{:else if !isAuthenticated}
  <Login onAuthenticated={handleAuthenticated} />
{:else}
  <QueryClientProvider client={queryClient}>
    <Shell onLogout={logout}>
      {#if bootError}
        <div class="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {bootError}
        </div>
      {/if}
      {#if current.tab === 'overview'}
        {#if session.isAdmin}
          <ProjectsOverviewPage />
        {:else}
          <EmptyState
            icon={FolderKanban}
            heading="No projects yet"
            sub="You haven't been granted access to any projects. Your admin will grant access as work is assigned to you."
          />
        {/if}
      {:else if current.tab === 'inbox'}
        {#if session.isAdmin}
          <TicketInboxPage />
        {:else}
          <EmptyState icon={ShieldAlert} heading="No access" sub="This section is only available to admins." />
        {/if}
      {:else if current.tab === 'deploys'}
        {#if session.isAdmin}
          <DeploysPage />
        {:else}
          <EmptyState icon={ShieldAlert} heading="No access" sub="This section is only available to admins." />
        {/if}
      {:else if current.tab === 'daily'}
        {#if session.isAdmin}
          <DailyActivityPage date={current.date} />
        {:else}
          <EmptyState icon={ShieldAlert} heading="No access" sub="This section is only available to admins." />
        {/if}
      {:else if current.tab === 'users'}
        {#if session.isAdmin}
          <UsersPage />
        {:else}
          <EmptyState icon={ShieldAlert} heading="No access" sub="This section is only available to admins." />
        {/if}
      {:else if current.tab === 'project'}
        <ProjectWorkspacePage projectId={current.projectId} />
      {:else if current.tab === 'ticket'}
        <TicketDetailPage ticketId={current.ticketId} />
      {:else if current.tab === 'doc'}
        <DocReaderPage docId={current.docId} />
      {:else if current.tab === 'settings'}
        <SettingsPage />
      {/if}
    </Shell>
    <CommandPalette />
  </QueryClientProvider>
{/if}
